import type { Conversation, Everything, MoteStatusPreview } from '@mote/api'
import { canAccessWebLocalStorage } from '@mote/helpers'
import { getMotePreview } from '@mote/recipes'
import { getMoteLogin } from '../../shared/Auth'

export type PartialConversationResponse = Everything & {
  conversation: Conversation
  moteStatus: MoteStatusPreview
}

export class MotePreviewFakeResponse {
  #preview: MoteStatusPreview | null = null
  #response: PartialConversationResponse | null = null

  constructor() {
    return this.#init()
  }

  #init() {
    try {
      const motePreview = getMotePreview()
      this.#preview = {
        inMotebook: false,
        translationLanguage: 'en-US',
        transcriptionLanguage: 'en-US',
        transcriptDisplay: '',
        ...motePreview.info,
        ...motePreview.transcript,
        ...motePreview.sticky,
        ...motePreview.audio,
        wasCreatedBySameUser: this.#getIsCreator(
          motePreview.info.createdByUserId
        ),
        visibility: 'public',
        isAnonymous: false,
        currentMoteId: motePreview.info.moteId,
        createdByGreeting: motePreview.info.createdByName.split(' ')[0],
        myReactions: [],
        availableReactions: [],
        availableUnreactions: []
      }
      this.#response = this.#getProperResponse(this.#preview)
    } catch {}

    return this
  }

  get response() {
    return this.#response
  }

  #getIsCreator(userId: string): boolean {
    if (!canAccessWebLocalStorage()) return false

    return getMoteLogin().userId === userId
  }

  #getProperResponse(
    moteStatus: MoteStatusPreview
  ): PartialConversationResponse {
    const userData = getMoteLogin()
    const isLoggedIn = !!userData?.token
    const recommendedTranscriptionLocale =
      userData.recommendedTranscriptionLocale || 'en-US'
    const recommendedUXLocale = userData.recommendedUXLocale || 'en-US'

    if (isLoggedIn) {
      return {
        auth: {
          isLoggedIn: true,
          accessToken: userData.accessToken,
          acquiredScopes: userData.acquiredScopes
        },
        goals: userData.goals,
        device: {
          affirmativeCarousel: userData.affirmativeCarousel,
          assistiveCarousel: userData.assistiveCarousel,
          key: '',
          recommendedTranscriptionLocale,
          recommendedUXLocale
        },
        conversation: {
          currentMoteId: moteStatus.moteId,
          canReply: false,
          replyMoteStatuses: []
        },
        moteStatus
      }
    }

    return {
      auth: { isLoggedIn: false },
      device: {
        affirmativeCarousel: [],
        assistiveCarousel: [],
        key: '',
        recommendedTranscriptionLocale,
        recommendedUXLocale
      },
      conversation: {
        currentMoteId: moteStatus.moteId,
        canReply: false,
        replyMoteStatuses: []
      },
      moteStatus
    }
  }
}
